import React, {useEffect, useState} from "react";
import styles from "./AdminContractInfo.module.css";
import Button from "../../../common/button/Button";
import {GetAdminContractResponseData} from "../../../../types/admin";
import {adminContractAPI, deleteContractAPI} from "../../../../services/admin";
import TextCard from "../../../common/textCard/TextCard";
import {useNavigate} from "react-router-dom";
import ExtendContractPopUp from "../../../../views/admin/popup/ExtendContractPopUp";
import {useAlert} from "../../../common/error/AlertContext";
import {ContractStatus} from "../../../../types/contract";

interface AdminClientInfoProps {
    contractId: number;
}

//어드민 계약 정보
const AdminContractInfo: React.FC<AdminClientInfoProps> = ({contractId}) => {
    const {showAlert} = useAlert();
    const [data, setData] = useState<GetAdminContractResponseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isExtendOpen, setIsExtendOpen] = useState(false);
    const navigate = useNavigate();
    const {showConfirm} = useAlert();

    //계약 연장 팝업 닫기
    const handleCloseExtendModal = () => {
        setIsExtendOpen(false);
        fetchData();
    }

    //계약 수정 페이지
    const handleEdit = () => {
        navigate(`/admin/clients/contract/${contractId}/edit`);
    };

    //계약 연장 팝업 열기
    const handleOpenExtendModal = () => {
        setIsExtendOpen(true);
    }

    //계약 삭제
    const handleDelete = async () => {
        //확인창
        showConfirm(
            "정말 삭제하시겠습니까?",
            async () => {
                try {
                    //삭제 API
                    await deleteContractAPI(contractId);
                    showAlert('삭제되었습니다.', "info");
                    //테이블 페이지로 이동
                    navigate('/admin/clients/contract');
                } catch (error) {
                    console.error('Error deleting contract:', error);
                    showAlert('삭제 중 오류가 발생했습니다.', "error");
                }
            }
        );
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //계약 정보 조회 API
            const response = await adminContractAPI(contractId);
            setData(response);
        } catch (error) {
            console.error('Error fetching account:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [contractId]);

    //날짜 데이터 없을 시 '-' 출력
    const formatDate = (date: Date | undefined) => {
        if (!date) return '-';
        return `${date}`;
    };

    //회사 정보
    const companyInfo = [
        {
            label: "회사명",
            value: data?.companyName || '-'
        },
        {
            label: "사업자등록번호",
            value: data?.businessNumber || '-'
        }
    ];

    //날짜 정보
    const dateInfo = [
        {
            label: "계약일",
            value: formatDate(data?.startedAt) || '-'
        },
        {
            label: "만료일",
            value: formatDate(data?.expireAt) || '-'
        },
        {
            label: "정기 결제일",
            value: `매월 ${data?.payday}일` || '-'
        }
    ];

    //계약 정보
    const contractInfo = [
        {
            label: "계약 기간",
            value: data?.period?.toString() + '개월' || '-'
        },
        {
            label: "계약 상태",
            value: data?.contractStatus?.toString() || '-'
        },
        {
            label: "작업자 단가",
            value: data?.unitCost ? `${data.unitCost.toLocaleString()}원` : '-'
        },
        {
            label: "작업자 수",
            value: data?.workerAmount ? `${data.workerAmount.toLocaleString()}명` : '-'
        }
    ];

    //계약 진행도 원형 그래프
    const CircularProgress = () => {
        const radius = 80;
        const circumference = 2 * Math.PI * radius;
        const progress = data?.progress || 0;
        const strokeDashoffset = circumference - (progress / 100) * circumference;

        return (
            <div className={styles.progressWrapper}>
                <svg className={styles.progressSvg} viewBox="0 0 192 192">
                    <circle
                        cx="96"
                        cy="96"
                        r={radius}
                        stroke="#E0E0E0"
                        strokeWidth="10"
                        fill="none"
                    />
                    <circle
                        cx="96"
                        cy="96"
                        r={radius}
                        stroke="#002387"
                        strokeWidth="10"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                    />
                </svg>
                <div className={styles.progressText}>
                    <span>{progress}%</span>
                </div>
            </div>
        );
    };

    if (loading || !data) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.licenseSection}>
                <div className={styles.buttonContainer}>
                    {/*예정 상태일 경우*/}
                    {data.contractStatus === ContractStatus.예정 && (
                        <>
                            {/*수정 버튼*/}
                            <Button
                                variant="secondary"
                                className={styles.button}
                                onClick={handleEdit}
                            >
                                수정
                            </Button>
                            {/*삭제 버튼*/}
                            <Button
                                variant="secondary"
                                className={styles.button}
                                onClick={handleDelete}
                            >
                                삭제
                            </Button>
                        </>
                    )}
                    {/*일 단위 계약 연장 버튼*/}
                    <Button
                        variant="primary"
                        className={styles.button}
                        onClick={handleOpenExtendModal}
                    >
                        계약일 연장
                    </Button>
                </div>
                <div className={styles.cardWrapper}>
                    <div className={styles.progressContainer}>
                        {/*원형 그래프*/}
                        <CircularProgress/>
                    </div>
                    <div className={styles.infoContainer}>
                        {/*계약 정보*/}
                        <TextCard items={companyInfo} className={styles.fullWidth}/>
                        <TextCard items={dateInfo} className={styles.fullWidth}/>
                        <TextCard items={contractInfo} className={styles.fullWidth}/>
                    </div>
                </div>
            </div>
            {/*계약 연장 팝업*/}
            <ExtendContractPopUp
                isOpen={isExtendOpen}
                onClose={handleCloseExtendModal}
                contractId={data.contractId}
                currentExpireDate={data.expireAt}
            />
        </div>
    );
}

export default AdminContractInfo;